import { FC, forwardRef, useEffect, useState } from 'react';
import * as Slider from '@radix-ui/react-slider';
import * as Select from '@radix-ui/react-select';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { FaIcon } from '@fe-learner-common';
import { useIntl } from 'react-intl';

type DurationRangeProps = {
  filterKey: string;
  label: string;
  min?: number;
  max?: number;
  onChange?: (range: [number, number]) => void;
  value?: [number, number];
  ariaLabelMinValue: string;
  ariaLabelMaxValue: string;
};

const SelectComp: FC<{
  value: number;
  values: number[];
  onChange: (value: number) => void;
}> = ({ value, values, onChange }) => {
  return (
    <Select.Root
      value={String(value)}
      onValueChange={(val) => onChange(parseInt(val))}
    >
      <Select.Trigger className="lf:l-tab lf:l-tab-md lf:l-tab-tertiary lf:w-full lf:text-body-md lf:text-neutral-500">
        <Select.Value>{value}</Select.Value>
        <FaIcon className="lf:ms-auto" icon={faChevronDown} />
      </Select.Trigger>
      <Select.Content
        position="popper"
        side="bottom"
        className="lf:z-[100] lf:max-h-[200px] lf:min-w-[var(--radix-select-trigger-width)] lf:rounded-xl lf:border lf:border-neutral-200 lf:bg-neutral-0 lf:shadow-100"
      >
        <Select.Viewport className="lf:flex lf:flex-col lf:gap-y-1 lf:p-2">
          {values.map((value) => (
            <Select.SelectItem
              key={value}
              value={value.toString()}
              className="lf:relative lf:flex lf:items-center lf:rounded-xl lf:bg-white lf:p-2 lf:text-title-sm lf:data-[highlighted]:bg-neutral-100 lf:data-[state=checked]:text-prominence-500  "
            >
              <Select.ItemText>{value}</Select.ItemText>
            </Select.SelectItem>
          ))}
        </Select.Viewport>
      </Select.Content>
    </Select.Root>
  );
};

export const DurationChoice = forwardRef<HTMLDivElement, DurationRangeProps>(
  (
    {
      label,
      filterKey,
      min = 0,
      max = 24,
      onChange,
      value: initialValue,
      ariaLabelMinValue,
      ariaLabelMaxValue,
    },
    ref,
  ) => {
    const intl = useIntl();
    const [value, setValue] = useState<[number, number]>(
      initialValue ?? [min, max],
    );

    const hoursOptions = Array.from({ length: max + 1 }, (_, i) => i);
    const handleSliderChange = (newValue: number[]) => {
      const range = newValue as [number, number];
      setValue(range);
      onChange?.(range);
    };

    useEffect(() => {
      if (initialValue) {
        setValue(initialValue);
      }
    }, [initialValue]);

    const handleDropdownChange = (index: 0 | 1, newValue: number) => {
      const newRange: [number, number] = [...value] as [number, number];
      if (index === 0) {
        newRange[0] = newValue;
        newRange[1] = Math.max(newRange[1], newValue + 1);
      } else {
        newRange[1] = newValue;
        newRange[0] = Math.min(newRange[0], newValue - 1);
      }
      setValue(newRange);
      onChange?.(newRange);
    };
    const ariaKey = `filter-group-${filterKey}`;
    return (
      <div ref={ref} role="group">
        <div className="lf:flex lf:items-center lf:justify-between lf:px-3 lf:py-3.5">
          <h2 className="lf:text-label-md lf:leading-5" id={ariaKey}>
            {label}
          </h2>
        </div>
        <div className="lf:space-y-4 lf:px-3">
          <div className="lf:space-y-2">
            <h3 className="lf:text-label-sm lf:text-neutral-500">
              {intl.formatMessage({
                id: 'learner.common.filter.duration.hours',
              })}
            </h3>
            <div className="lf:flex lf:items-center lf:gap-2">
              <SelectComp
                value={value[0]}
                values={hoursOptions.slice(0, max)}
                onChange={(val) => {
                  handleDropdownChange(0, val);
                }}
              />
              <span>-</span>
              <SelectComp
                value={value[1]}
                values={hoursOptions.slice(Math.max(value[0] + 1, 1))}
                onChange={(val) => {
                  handleDropdownChange(1, val);
                }}
              />
            </div>
          </div>

          <Slider.Root
            className="lf:relative lf:flex lf:w-full lf:touch-none lf:select-none lf:items-center  lf:py-4"
            min={min}
            max={max}
            value={value}
            onValueChange={handleSliderChange}
            step={1}
          >
            <Slider.Track className="lf:relative lf:h-1 lf:w-full lf:grow lf:rounded-full lf:bg-neutral-200">
              <Slider.Range className="lf:absolute lf:h-full lf:rounded-full lf:bg-prominence-500" />
            </Slider.Track>
            <Slider.Thumb
              className="lf:block lf:h-4 lf:w-1 lf:rounded-xs lf:bg-prominence-500 lf:focus:outline-hidden lf:focus:ring-2 lf:focus:ring-prominence-500 lf:focus:ring-offset-2"
              aria-label={ariaLabelMinValue}
            />
            <Slider.Thumb
              className="lf:block lf:h-4 lf:w-1 lf:rounded-xs lf:bg-prominence-500 lf:focus:outline-hidden lf:focus:ring-2 lf:focus:ring-prominence-500 lf:focus:ring-offset-2"
              aria-label={ariaLabelMaxValue}
            />
          </Slider.Root>
        </div>
      </div>
    );
  },
);
DurationChoice.displayName = 'DurationChoice';
